import { useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../loading/LoadingSpinner';
import { useLocale } from '../../context';
import {
  formatPromoTimeLeft,
  getPromoLink,
  getPromoPageHtml,
  getPromoModalType,
  isTournamentPromo,
} from './promoUtils';

function IconTimer() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" width="14" height="14" aria-hidden="true">
      <path fill="currentColor" fillOpacity="0.64" fillRule="evenodd" d="M7 12.833A5.83 5.83 0 0 1 1.167 7 5.835 5.835 0 0 1 7 1.167 5.83 5.83 0 0 1 12.834 7 5.83 5.83 0 0 1 7 12.833m1.861-3.669q.105.063.228.064a.44.44 0 0 0 .222-.817L7.233 7.176V4.48a.437.437 0 1 0-.875 0v2.946c0 .152.081.292.215.373z" clipRule="evenodd" />
    </svg>
  );
}

function stripHtml(value) {
  return String(value || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isPromoInteractive(promo) {
  if (!promo) return false;
  if (getPromoModalType(promo) === 'welcome-landing') return true;
  if (getPromoModalType(promo) === 'tournament') return true;
  if (getPromoLink(promo) || getPromoPageHtml(promo)) return true;
  if (promo.title || promo.detailTitle || (promo.terms && promo.terms.length)) return true;
  return false;
}

function resolvePromoCtaLabel(promo, tr) {
  const custom = String(promo.ctaLabel || promo.cta || '').trim();
  if (custom) return custom;

  if (isTournamentPromo(promo)) return tr('home.promo.cta.join');

  const link = getPromoLink(promo).toLowerCase();
  if (
    getPromoModalType(promo) === 'welcome-landing'
    || /register|signup|sign-up|kayit|kayıt|\/auth/i.test(link)
  ) {
    return tr('home.promo.cta.signup');
  }

  if (
    promo.type === 'casino'
    || /\/(casino|slots|live)/i.test(link)
  ) {
    return tr('home.promo.cta.play');
  }

  return tr('home.promo.cta.join');
}

function resolveBannerCopy(promo, tr) {
  const isTournament = isTournamentPromo(promo);
  const uptitle = stripHtml(promo.uptitle || promo.caption);
  const prizeText = stripHtml(promo.prizeMain || '');
  const name = stripHtml(promo.name || promo.detailTitle);
  const subtitle = stripHtml(promo.sub || promo.subtitle || promo.detailSubtitle || promo.prizeSub);
  const timer = formatPromoTimeLeft(promo, tr);

  if (isTournament && name) {
    return {
      uptitle,
      titleHtml: promo.name || name,
      titleIsHtml: false,
      subtitle: subtitle || prizeText,
      showTimer: Boolean(timer),
      timer,
    };
  }

  return {
    uptitle,
    titleHtml: promo.title || name || '',
    titleIsHtml: Boolean(promo.title),
    subtitle,
    showTimer: Boolean(timer),
    timer,
  };
}

export function PromoCardPlaceholder() {
  return (
    <div className="promo-card">
      <div className="promo-card__slide">
        <div className="promo-card__placeholder promo-card__placeholder--spinner" aria-busy="true">
          <LoadingSpinner size="md" />
        </div>
      </div>
    </div>
  );
}

export default function PromoCard({ promo, onDetails, variant = 'carousel' }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const variantClass = variant === 'page'
    ? ' promo-card--page'
    : variant === 'mobile'
      ? ' promo-card--mobile'
      : variant === 'bside'
        ? ' promo-card--bside'
        : '';
  const [loaded, setLoaded] = useState(false);
  const interactive = isPromoInteractive(promo);
  const copy = useMemo(() => resolveBannerCopy(promo, tr), [promo, tr]);
  const ctaLabel = resolvePromoCtaLabel(promo, tr);
  const link = getPromoLink(promo);
  const href = link || '#';
  const hasOverlayCopy = Boolean(copy.uptitle || copy.titleHtml || copy.subtitle || copy.showTimer);

  useEffect(() => {
    if (!promo.img) {
      setLoaded(true);
      return undefined;
    }

    let cancelled = false;
    setLoaded(false);

    const img = new Image();
    const finish = () => {
      if (!cancelled) setLoaded(true);
    };

    img.onload = finish;
    img.onerror = finish;
    img.src = promo.img;

    if (img.complete) finish();

    return () => {
      cancelled = true;
    };
  }, [promo.id, promo.img]);

  const open = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (interactive) onDetails?.(promo);
  };

  return (
    <div
      className={`promo-card${variantClass}${interactive ? ' is-interactive' : ''} promo-card--banner`}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={() => {
        if (interactive) onDetails?.(promo);
      }}
      onKeyDown={(e) => {
        if (!interactive) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onDetails?.(promo);
        }
      }}
    >
      <div className="promo-card__slide">
        <div className={`promo-card__spinner-wrap${loaded ? ' is-hidden' : ''}`} aria-hidden={loaded}>
          <LoadingSpinner size="md" />
        </div>
        <section
          className={`promo-card__bg${loaded ? ' is-loaded' : ''}`}
          style={{
            backgroundColor: promo.bg || 'var(--surface-panel, #1a1a1a)',
            backgroundImage: loaded && promo.img ? `url(${promo.img})` : undefined,
            backgroundPosition: variant === 'mobile' ? 'right center' : undefined,
          }}
        >
          <section className="promo-card__content">
            <div className="promo-card__info">
              {copy.uptitle ? (
                <p className="promo-card__uptitle">{copy.uptitle}</p>
              ) : null}
              {copy.titleHtml ? (
                copy.titleIsHtml ? (
                  <p className="promo-card__title" dangerouslySetInnerHTML={{ __html: copy.titleHtml }} />
                ) : (
                  <p className="promo-card__title">{copy.titleHtml}</p>
                )
              ) : null}
              {copy.subtitle ? (
                <p className="promo-card__subtitle">{copy.subtitle}</p>
              ) : null}
              {copy.showTimer && (
                <p className="promo-card__timer">
                  <IconTimer />
                  <span>{copy.timer}</span>
                </p>
              )}
            </div>
            {(hasOverlayCopy || interactive) ? (
              <div className="promo-card__ctas">
                <a
                  href={href}
                  className="promo-card__btn"
                  onClick={open}
                >
                  {ctaLabel}
                </a>
              </div>
            ) : null}
          </section>
        </section>
      </div>
    </div>
  );
}
